import * as React from 'react'
import { Plus, Sparkles, Trash2, X } from 'lucide-react'

import {
  useAnalyzeLabel, useCreateLabel, useDeleteLabel, useLabels, useUpdateLabel,
  type LabelSemantics,
} from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FilterSelect } from '@/components/ui/filter-select'
import { Dialog, DialogHeader, DialogTitle, DrawerContent } from '@/components/ui/dialog'
import { EmptyState, LoadingState } from '@/components/ui/states'
import { Tooltip } from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/toast'
import { cn, formatNumber } from '@/lib/utils'

const PAGE_SIZE = 50

const SOURCE_OPTIONS = [
  { value: 'library', label: '来自域名库' },
  { value: 'manual', label: '手动添加' },
]

const STATE_OPTIONS = [
  { value: 'ai', label: '已有 AI 释义' },
  { value: 'no-ai', label: '尚无 AI 释义' },
  { value: 'manual', label: '有人工内容' },
]

/**
 * 语义库：一个主体一行，一份释义覆盖它的所有后缀。
 *
 * Keyed by the label because that is what the meaning belongs to. abc.com and
 * abc.xyz read identically, and the old per-domain storage paid the model twice
 * for one answer and left two rows free to disagree about it.
 */
export default function SemanticsPage() {
  const { user } = useAuth()
  const canEdit = user?.role === 'admin'
  const toast = useToast()

  const [q, setQ] = React.useState('')
  const [tag, setTag] = React.useState('')
  const [source, setSource] = React.useState('')
  const [state, setState] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [editing, setEditing] = React.useState<LabelSemantics | null>(null)
  const [newLabel, setNewLabel] = React.useState('')

  // Typing must not fire a request per keystroke against a 27k-row table.
  const [debounced, setDebounced] = React.useState('')
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300)
    return () => clearTimeout(t)
  }, [q])
  React.useEffect(() => setPage(0), [debounced, tag, source, state])

  const { data, isLoading } = useLabels({
    q: debounced || undefined,
    tag: tag || undefined,
    source: source || undefined,
    has_ai: state === 'ai' ? true : state === 'no-ai' ? false : undefined,
    manual: state === 'manual' ? true : undefined,
    sort: 'label',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  const create = useCreateLabel()
  const analyze = useAnalyzeLabel()
  const remove = useDeleteLabel()

  const total = data?.total ?? 0
  const pages = Math.ceil(total / PAGE_SIZE)

  async function addLabel() {
    const value = newLabel.trim()
    if (!value) return
    try {
      const created = await create.mutateAsync(value)
      setNewLabel('')
      toast.success(`已添加 ${created.label}`)
      setEditing(created)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '添加失败')
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-medium">语义库</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          按域名主体维护中文释义。一个主体一份，abc.com 和 abc.xyz 共用同一条 ——
          释义有规则算的、模型给的、你自己填的，你填的优先；标签只来自你的标签规则和手填。
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索主体…"
          className="h-9 w-56"
        />
        <SingleFilter
          label="标签"
          value={tag}
          onChange={setTag}
          options={(data?.tags ?? []).map((t) => ({ value: t, label: t }))}
        />
        <SingleFilter label="来源" value={source} onChange={setSource} options={SOURCE_OPTIONS} />
        <SingleFilter label="状态" value={state} onChange={setState} options={STATE_OPTIONS} />
        {(tag || source || state || q) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-xs"
            onClick={() => { setQ(''); setTag(''); setSource(''); setState('') }}
          >
            清除
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {canEdit && (
            <>
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addLabel()}
                placeholder="新增主体，如 abcdtyu"
                className="h-9 w-48"
              />
              <Button size="sm" className="h-9" onClick={addLabel} disabled={create.isPending}>
                <Plus className="mr-1 h-3.5 w-3.5" />添加
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingState rows={6} label="正在加载语义库" />
      ) : !data?.items.length ? (
        <EmptyState title="没有匹配的主体" description="换个搜索条件，或手动添加一个。" />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">主体</th>
                <th className="px-3 py-2 text-left font-medium">释义</th>
                <th className="px-3 py-2 text-left font-medium">标签</th>
                <th className="px-3 py-2 text-right font-medium">域名数</th>
                <th className="px-3 py-2 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <LabelRow
                  key={row.label}
                  row={row}
                  canEdit={canEdit}
                  onEdit={() => setEditing(row)}
                  onAnalyze={async () => {
                    try {
                      await analyze.mutateAsync({ label: row.label, body: undefined })
                      toast.success(`${row.label} 已排队，稍后自动出结果`)
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : '排队失败')
                    }
                  }}
                  onDelete={async () => {
                    try {
                      await remove.mutateAsync({ label: row.label, body: undefined })
                      toast.success(`已删除 ${row.label}`)
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : '删除失败')
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>共 {formatNumber(total)} 个主体</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              上一页
            </Button>
            <span className="tabular-nums">{page + 1} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
              下一页
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <LabelEditor
          key={editing.label}
          row={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

/** 一行。释义列同时说明这句话是谁给的。 */
function LabelRow({
  row, canEdit, onEdit, onAnalyze, onDelete,
}: {
  row: LabelSemantics
  canEdit: boolean
  onEdit: () => void
  onAnalyze: () => void
  onDelete: () => void
}) {
  const queued = !!row.ai_queued_at
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="px-3 py-2">
        <button className="font-medium hover:underline" onClick={onEdit}>{row.label}</button>
        {row.source === 'manual' && (
          <Badge variant="outline" className="ml-2 text-[10px]">手动</Badge>
        )}
      </td>
      <td className="px-3 py-2">
        {row.effective_reading || row.effective_meaning ? (
          <div className="flex items-start gap-2">
            <SourceMark source={row.reading_source} />
            <div className="min-w-0">
              {row.effective_reading && <div className="truncate">{row.effective_reading}</div>}
              {row.effective_meaning && (
                <div className="truncate text-xs text-muted-foreground">{row.effective_meaning}</div>
              )}
            </div>
          </div>
        ) : row.ai_error ? (
          <Tooltip content={row.ai_error}>
            <span className="text-xs text-amber-600">分析失败</span>
          </Tooltip>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {row.effective_tags.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
          {row.effective_tags.map((t) => (
            <Badge
              key={t}
              variant={row.tags ? 'default' : 'muted'}
              className="text-[10px]"
            >
              {t}
            </Badge>
          ))}
        </div>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-xs text-muted-foreground">
        {row.domain_count}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1">
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={onAnalyze}
              disabled={queued}
            >
              <Sparkles className={cn('mr-1 h-3.5 w-3.5', queued && 'animate-pulse')} />
              {queued ? '排队中' : 'AI 释义'}
            </Button>
          )}
          {canEdit && row.source === 'manual' && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

/**
 * Which of the three sources produced the reading on this row.
 *
 * Shown because the three are not equally trustworthy: the rules are
 * reproducible, the model is not, and a person's own entry outranks both. A
 * reader deciding whether to act on 啊 needs to know which one said it.
 */
function SourceMark({ source }: { source?: string }) {
  if (!source) return null
  const map: Record<string, { text: string; className: string }> = {
    manual: { text: '人', className: 'bg-emerald-100 text-emerald-700' },
    ai: { text: 'AI', className: 'bg-violet-100 text-violet-700' },
    rule: { text: '规', className: 'bg-slate-100 text-slate-600' },
  }
  const m = map[source]
  if (!m) return null
  return (
    <span className={cn('mt-0.5 shrink-0 rounded px-1 text-[10px] leading-4', m.className)}>
      {m.text}
    </span>
  )
}

/** 右侧编辑抽屉。 */
function LabelEditor({ row, onClose }: { row: LabelSemantics; onClose: () => void }) {
  const update = useUpdateLabel()
  const analyze = useAnalyzeLabel()
  const toast = useToast()

  const [reading, setReading] = React.useState(row.manual_reading ?? '')
  const [meaning, setMeaning] = React.useState(row.manual_meaning ?? '')
  const [note, setNote] = React.useState(row.manual_note ?? '')
  // Seeded from what the table shows, so editing starts from the rule tags
  // rather than from nothing — the point of "手动优先，规则兜底" is that taking
  // over should not mean retyping what was already right.
  const [tags, setTags] = React.useState<string[]>(row.effective_tags)
  const [tagInput, setTagInput] = React.useState('')

  const ruleTagsStillApply = row.tags === null

  async function save() {
    try {
      await update.mutateAsync({
        label: row.label,
        body: {
          manual_reading: reading,
          manual_meaning: meaning,
          manual_note: note,
          tags,
        },
      })
      toast.success('已保存')
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    }
  }

  function addTag(value: string) {
    const t = value.trim()
    if (t && !tags.includes(t)) setTags([...tags, t])
    setTagInput('')
  }

  const aiReading = row.reading_source === 'ai' ? row.effective_reading : undefined

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DrawerContent className="sm:max-w-lg">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle className="text-sm">语义 · {row.label}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto p-5 text-sm">
        <div className="rounded-md border bg-muted/30 p-3 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">覆盖 {row.domain_count} 个域名</span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={!!row.ai_queued_at}
              onClick={async () => {
                try {
                  await analyze.mutateAsync({ label: row.label, body: undefined })
                  toast.success('已排队')
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : '排队失败')
                }
              }}
            >
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              {row.ai_queued_at ? '排队中' : '让 AI 重新释义'}
            </Button>
          </div>
          {(row.note || aiReading) && <div className="mt-2 space-y-1">
            {aiReading && (
              <div><span className="text-muted-foreground">模型：</span>{aiReading}
                {row.effective_meaning && ` —— ${row.effective_meaning}`}</div>
            )}
            {row.note && <div><span className="text-muted-foreground">规则：</span>{row.note}</div>}
          </div>}
          {row.ai_error && <div className="mt-2 text-amber-600">上次分析失败：{row.ai_error}</div>}
        </div>

        <Field label="释义" hint="填了就以你的为准，模型和规则都不会覆盖">
          <Input value={reading} onChange={(e) => setReading(e.target.value)} placeholder="如 爱彼此" />
        </Field>
        <Field label="说明">
          <Input value={meaning} onChange={(e) => setMeaning(e.target.value)} placeholder="展开解释这个主体" />
        </Field>
        <Field label="备注" hint="商标风险、谐音禁忌等">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        <Field
          label="标签"
          hint={ruleTagsStillApply
            ? '当前由规则给出。一旦保存，这个主体的标签就以你填的为准。'
            : '已由你接管，规则不再参与。'}
        >
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
              {tags.map((t) => (
                <Badge key={t} variant="default" className="gap-1 text-[10px]">
                  {t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {tags.length === 0 && <span className="text-xs text-muted-foreground">无标签</span>}
            </div>
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput) }
              }}
              placeholder="输入后回车添加"
              className="h-8"
            />
          </div>
        </Field>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" size="sm" onClick={onClose}>取消</Button>
          <Button size="sm" onClick={save} disabled={update.isPending}>保存</Button>
        </div>
        </div>
      </DrawerContent>
    </Dialog>
  )
}

/**
 * One-of filter over the shared multi-select control.
 *
 * These three narrow to a single value, so the array the control speaks in is
 * either empty or one long; picking a second entry replaces the first rather
 * than adding to it.
 */
function SingleFilter({
  label, value, onChange, options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <FilterSelect
      label={label}
      options={options}
      selected={value ? [value] : []}
      onToggle={(v) => onChange(v === value ? '' : v)}
      onClear={() => onChange('')}
    />
  )
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium">{label}</div>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}
